/*
 * Toteme search, powered by Depict GPT search (VROOM-283).
 *
 * Drop-in replacement for the Foundation `depict-search.js`: same `<depict-search>`
 * element in both formats — the header drawer (`data-format="drawer"`) and the full
 * /search results page (`data-format="page"`) — with the same BAO product-card +
 * filter renderers, but the data layer calls Depict's GPT endpoints:
 *   - results:      POST https://runway.depict.ai/search_v2   (colon-delimited stream)
 *   - autocomplete: GET  https://runway.depict.ai/autocomplete_v2  (drawer only)
 * The GPT engine is agentic: a full natural-language query ("a dress for a wedding")
 * resolves server-side and streams the matching products under `keyword`.
 *
 * Merchant is the GPT identity (`search-<shopId>`) passed from Liquid via
 * data-merchant-id. Products render identically by mapping the GPT product shape into
 * the `formattedProduct` shape BAO.Depict.depictProductCard consumes. Facets: search_v2
 * returns `filter_facets` (field names); option values + counts are derived from the
 * returned products, and selections are sent back as filters:[{field, operator, value}].
 */
const { debounce } = window.BAO.Utils
const { depictProductCard, depictFilters } = window.BAO.Depict

const RUNWAY_BASE = 'https://runway.depict.ai'
const SEARCH_REPORTING_URL =
  'https://depict-search.apps.depict.ai/api/search-reporting'
const MIN_QUERY_LENGTH = 2
const PAGINATION_THRESHOLD_PX = 600

// The GPT payload's `colors` are granular shade names ("Jet Black", "Buttercup
// Yellow"). The live site filters by colour FAMILY (Foundation's klevu families),
// so collapse shades for display and expand back to raw shades when filtering.
// Family match = whole word in the shade name; unmatched shades pass through.
const COLOUR_FAMILIES = [
  'beige',
  'black',
  'blue',
  'brown',
  'creme',
  'cream',
  'gold',
  'green',
  'grey',
  'gray',
  'navy',
  'orange',
  'pink',
  'purple',
  'red',
  'silver',
  'white',
  'yellow',
]
// Display umbrella for spelling variants (cream -> creme, gray -> grey).
const FAMILY_CANONICAL = { cream: 'creme', gray: 'grey' }
const colourFamilyOf = (shade) => {
  const words = String(shade)
    .toLowerCase()
    .split(/[^a-z]+/)
  // Last matching family word wins: "Off-White White" -> white, "Mid Grey Melange" -> grey.
  for (let i = words.length - 1; i >= 0; i--) {
    if (COLOUR_FAMILIES.includes(words[i])) {
      return FAMILY_CANONICAL[words[i]] || words[i]
    }
  }
  return String(shade)
}

// Facet field -> how to pull its values off a product, and its label i18n key.
// Labels come from window.theme.strings (set in Liquid) so they localize.
const FACETS = [
  {
    field: 'product_type',
    extract: (p) => (p.product_type ? [p.product_type] : []),
  },
  { field: 'colors', extract: (p) => (p.colors || []).map(colourFamilyOf) },
  { field: 'material', extract: (p) => (p.material ? [p.material] : []) },
  // gender deliberately absent: runway declares it as a facet, but Toteme is a
  // womenswear catalogue so it renders as a useless single-value group (BAO nit).
  { field: 'pattern', extract: (p) => (p.pattern ? [p.pattern] : []) },
  { field: 'occasions', extract: (p) => p.occasions || [] },
  { field: 'style_attributes', extract: (p) => p.style_attributes || [] },
]
const FACET_BY_FIELD = Object.fromEntries(FACETS.map((f) => [f.field, f]))

const titleCase = (s) =>
  String(s).replace(/(^|\s|-)\S/g, (c) => c.toUpperCase())

// Escape untrusted (API/model-supplied) strings before they reach innerHTML.
// Covers HTML text and double/single-quoted attribute contexts.
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

class DepictGptSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.query = ''
    this.searchProducts = []
    this.page = 1
    this.hasMore = false
    this.allProductsLoaded = false
    this.isLoadingPagination = false
    this.isTicking = false

    this.facetFields = []
    this.selectedFilters = []
    this.expandedFilters = []
    this.currentSearchId = null
    this.requestSeq = 0
    this.abortController = null

    this.paginationSentinel = null
    this.paginationObserver = null

    this.onInputChange = this.onInputChange.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)

    this.setupPaginationObserver()

    // On the results page the query comes from the URL, not an input. Kick the
    // search off here (after super.connectedCallback has run setupListeners) so
    // listeners are wired and this.query is set before the first fetch.
    if (this.searchFormat === 'page') {
      this.query = new URLSearchParams(window.location.search).get('q') || ''
      if (this.query) this.getSearchResults('default')
    }
  }

  disconnectedCallback () {
    this.paginationObserver?.disconnect()
    this.paginationSentinel?.remove()
    super.disconnectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.onClick)
    this.listeners.add(this, 'change', this.onFilterChange)
    if (this.el('filters-clear')) {
      this.listeners.add(this.el('filters-clear'), 'click', () =>
        this.resetAllFilters(),
      )
    }

    if (this.searchFormat === 'drawer') {
      // 250ms: BAO flagged the search as feeling slow — most of the latency is
      // the API round-trip, so keep the input debounce tight.
      this.debouncedOnInputChange = debounce(() => this.onInputChange(), 250)
      this.listeners.add(
        this.el('input'),
        'input',
        this.debouncedOnInputChange,
      )
      // GPT autocomplete only returns query suggestions — hide the unused
      // content-page and collection suggestion blocks so no empty headings show.
      for (const name of ['content-suggestions', 'collections']) {
        const block =
          this.el(name) && this.el(name).closest('.drw-Search_Block')
        if (block) block.style.display = 'none'
      }
    }
  }

  // Safe element accessor — formats expose different sub-elements.
  el (name) {
    return this.els[name] ? this.els[name].element : null
  }

  get searchFormat () {
    return this.dataset.format || 'drawer'
  }

  get classPrefix () {
    return this.searchFormat === 'page' ? 'prd-List' : 'drw-Search'
  }

  get merchantId () {
    return this.dataset.merchantId
  }

  get locale () {
    return this.dataset.locale || window.theme.depict.language
  }

  get country () {
    return this.dataset.country || window.theme.depict.country
  }

  get sessionId () {
    // Canonical key shared with Depict's Web Pixel (gpt-search-tracking reads
    // `depictGptSearchSessionId` as the `dsid` on add-to-cart / purchase events).
    // Using the same value here ties our search_v2 + search-reporting session to
    // the pixel's behavioural events so cart/purchase attribution can join back.
    let id = sessionStorage.getItem('depictGptSearchSessionId')
    if (!id) {
      id = self.crypto.randomUUID()
      sessionStorage.setItem('depictGptSearchSessionId', id)
    }
    return id
  }

  facetLabel (field) {
    const strings =
      (window.theme.strings && window.theme.strings.searchFacets) || {}
    return strings[field] || titleCase(field.replace(/_/g, ' '))
  }

  onInputChange () {
    this.query = this.el('input').value.trim()
    this.page = 1
    this.selectedFilters = []
    this.getSearchResults('default')
  }

  onClick (event) {
    const suggestionEl = event.target.closest('[data-gpt-suggestion]')
    if (suggestionEl) {
      event.preventDefault()
      const accepted = suggestionEl.dataset.gptSuggestion
      this.reportEvents([
        {
          ...this.baseEvent(),
          event_type: 'autocomplete_accept',
          search_id: this.currentSearchId,
          input_value: this.el('input') ? this.el('input').value : this.query,
          accepted_suggestion: accepted,
        },
      ])
      this.query = accepted
      if (this.el('input')) this.el('input').value = this.query
      this.page = 1
      this.selectedFilters = []
      this.getSearchResults('default')
      return
    }

    if (event.target.closest('[data-gpt-retry]')) {
      event.preventDefault()
      this.getSearchResults('default')
      return
    }

    // Product click — fire before navigation (keepalive delivers it).
    const card = event.target.closest('product-card')
    if (card && card.dataset.id) {
      this.reportEvents([
        {
          ...this.baseEvent(),
          event_type: 'product_click',
          search_id: this.currentSearchId,
          product_id: card.dataset.id,
          query: this.query,
          position: Number(card.closest('[data-index]')?.dataset.index ?? 0),
        },
      ])
    }

    const moreToggle = event.target.closest(
      '[data-depict-search-el="more-toggle"]',
    )
    if (moreToggle) this.handleMoreLessToggle(moreToggle)
  }

  async getSearchResults (action = 'default') {
    if (!this.query || this.query.length < MIN_QUERY_LENGTH) {
      this.resetSearch()
      return
    }

    if (action === 'default') {
      this.page = 1
      this.allProductsLoaded = false
      this.isLoadingPagination = false
      this.currentSearchId = self.crypto.randomUUID()
      // Cancel any in-flight default request so its (older) response can't land.
      if (this.abortController) this.abortController.abort()
      this.abortController = new AbortController()
      this.setLoading(true)
      // Reveal the results view up front so the loading spinner is visible
      // (the products panel is hidden until the first result set otherwise).
      if (this.searchFormat === 'drawer') {
        this.classList.add('drw-Search-showResults')
        this.el('default-panel').setAttribute('aria-hidden', true)
        this.el('products-panel').setAttribute('aria-hidden', false)
        this.el('suggestions-panel').setAttribute('aria-hidden', false)
      }
    }

    // Sequence guard: only the latest request may write results.
    const seq = ++this.requestSeq
    const searchId = this.currentSearchId
    const signal =
      action === 'default' ? this.abortController.signal : undefined

    const filters = this.selectedFilters.map((f) => {
      let value = f.values
      if (f.key === 'colors') {
        // The drawer shows colour FAMILIES but the API filters raw shade names —
        // expand each selected family to every shade seen for it. Shades are
        // remembered across responses so paginating can't shrink the expansion;
        // the family word itself is included since some shades ARE the family.
        const known = (this.knownColourShades ||= new Set())
        for (const p of this.searchProducts) {
          for (const s of p.colors || []) known.add(s)
        }
        const wanted = new Set(f.values)
        value = [
          ...new Set([
            ...[...known].filter((s) => wanted.has(colourFamilyOf(s))),
            ...f.values,
          ]),
        ]
      }
      return { field: f.key, operator: 'in', value }
    })

    const searchPromise = fetch(`${RUNWAY_BASE}/search_v2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
      },
      signal,
      body: JSON.stringify({
        query: this.query,
        merchant_id: this.merchantId,
        session_id: this.sessionId,
        search_id: searchId,
        locale: this.locale,
        country: this.country,
        page: this.page,
        filters: filters.length ? filters : null,
      }),
    }).then((r) => {
      // A non-2xx (500/429/503) still resolves; surface it as the error/retry
      // state instead of parsing the error body into a false "No results".
      if (!r.ok) throw new Error(`search_v2 responded ${r.status}`)
      return r.text()
    })

    const autocompletePromise =
      this.searchFormat === 'drawer'
        ? fetch(
            `${RUNWAY_BASE}/autocomplete_v2?${new URLSearchParams({
              query: this.query,
              merchant_id: this.merchantId,
              locale: this.locale,
              country: this.country,
            })}`,
            { signal },
        )
          .then((r) => r.json())
          .catch(() => ({ suggestions: [] }))
        : Promise.resolve({ suggestions: [] })

    try {
      const [searchText, autocomplete] = await Promise.all([
        searchPromise,
        autocompletePromise,
      ])
      if (seq !== this.requestSeq) return // a newer request superseded this one
      this.handleSearchResults({
        parsed: this.parseSearchStream(searchText),
        autocomplete,
        action,
        searchId,
      })
    } catch (error) {
      if (error && error.name === 'AbortError') return // intentionally cancelled
      if (seq !== this.requestSeq) return
      this.renderError(action)
    }
  }

  // --- Search reporting (analytics) ---------------------------------------
  baseEvent () {
    return {
      merchant_id: this.merchantId,
      session_id: this.sessionId,
      locale: this.locale,
      country: this.country,
    }
  }

  reportEvents (events) {
    if (!events.length) return
    // text/plain avoids a CORS preflight; keepalive survives navigation on click.
    fetch(SEARCH_REPORTING_URL, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(events),
    }).catch(() => {})
  }

  /** search_v2 streams newline-delimited `key:jsonValue` records. */
  parseSearchStream (text) {
    const out = { products: [], pageInfo: {}, facetFields: [] }
    for (const line of text.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed) continue
      const colon = trimmed.indexOf(':')
      if (colon === -1) continue
      const key = trimmed.slice(0, colon)
      let value
      try {
        value = JSON.parse(trimmed.slice(colon + 1))
      } catch {
        continue
      }
      if (key === 'keyword') out.products = value
      else if (key === 'keyword_page_info') out.pageInfo = value
      else if (key === 'filter_facets') out.facetFields = value
    }
    return out
  }

  formatGptProduct (product) {
    const root = window.Shopify.routes.root
    const base =
      root === '/'
        ? `//${location.host}`
        : `//${location.host}${root.replace(/\/$/, '')}`

    // Escape everything from the (untrusted) search corpus before it reaches HTML.
    const title = escapeHtml(product.title)
    const handle = escapeHtml(product.handle)
    const currency = escapeHtml(product.currency)

    return {
      id: escapeHtml(product.id),
      handle,
      title,
      url: `${base}/products/${handle}`,
      productListingResultId: escapeHtml(product.id),
      available: !product.out_of_stock,
      featuredImageIsFlat: false,
      tags: (product.tags || []).map((tag) => escapeHtml(tag)),
      price: { amount: Number(product.price) * 100, currencyCode: currency },
      compareAtPrice: {
        amount: Number(product.original_price || product.price) * 100,
        currencyCode: currency,
      },
      media: (product.image_urls || []).map((src) => ({
        src: escapeHtml(src),
        alt: title,
        media_type: 'image',
      })),
    }
  }

  handleSearchResults ({ parsed, autocomplete, action, searchId }) {
    const products = parsed.products || []
    this.setLoading(false)

    if (action === 'offset') {
      this.searchProducts = [...this.searchProducts, ...products]
    } else {
      this.searchProducts = products
      this.facetFields = parsed.facetFields || []
    }
    this.hasMore = !!parsed.pageInfo.has_next_page

    // Products (shared)
    const cardsHTML = this.searchProducts
      .map((product, index) => this.getProductCardHTML(product, index))
      .filter(Boolean)
      .join('')

    if (action === 'default' && this.searchFormat === 'drawer') {
      this.el('products-panel').scrollTo({ top: 0, behavior: 'instant' })
    }

    this.el('product-grid').innerHTML = cardsHTML || this.emptyStateHTML()

    // Facets — build only on a fresh search so options don't shift while paginating.
    if (action === 'default' && this.el('filters')) {
      this.el('filters').innerHTML = depictFilters({
        filters: this.buildFilters(),
        expandedFilters: this.expandedFilters,
        format: 'search',
      })
    }
    if (this.el('filters-clear')) {
      const noFilters = this.selectedFilters.length === 0
      // Set inert FIRST: it synchronously blurs the button (focus is reset when an
      // element becomes inert), so aria-hidden is never applied to a focused element.
      this.el('filters-clear').toggleAttribute('inert', noFilters)
      this.el('filters-clear').setAttribute('aria-hidden', noFilters)
    }

    // Counts. search_v2 gives no total, so signal "more exist" with a trailing +.
    const countLabel = `${this.searchProducts.length}${this.hasMore ? '+' : ''}`
    if (this.el('filters-close')) {
      this.el('filters-close').textContent =
        window.theme.strings.showCountItems.replace('[count]', countLabel)
    }

    if (this.searchFormat === 'drawer') {
      const suggestions = (autocomplete && autocomplete.suggestions) || []
      this.el('suggestions').innerHTML = this.getSuggestionsHTML(suggestions)

      this.classList.toggle('drw-Search-showResults', true)
      this.el('default-panel').setAttribute('aria-hidden', true)
      this.el('products-panel').setAttribute('aria-hidden', false)
      this.el('suggestions-panel').setAttribute('aria-hidden', false)
      if (this.el('clear-panel')) {
        this.el('clear-panel').classList.toggle(
          'drw-Search_Buttons-hasFilters',
          this.selectedFilters.length > 0,
        )
      }
    } else if (this.el('results-count')) {
      this.el('results-count').textContent =
        window.theme.strings.showingCountItems.replace('[count]', countLabel)
    }

    // Impressions for the products rendered in this pass, stamped with THIS
    // response's search_id (not the live field, which a newer search may own).
    const from =
      action === 'offset' ? this.searchProducts.length - products.length : 0
    this.reportEvents(
      products.map((product, i) => ({
        ...this.baseEvent(),
        event_type: 'product_impression',
        search_id: searchId,
        product_id: String(product.id),
        position: from + i,
      })),
    )

    this.allProductsLoaded = !this.hasMore
    if (action === 'offset') this.isLoadingPagination = false
  }

  emptyStateHTML () {
    const msg = (
      window.theme.strings.searchNoResults || 'No results for "[query]".'
    ).replace('[query]', escapeHtml(this.query))
    return `<li class="${this.classPrefix}_Item ${this.classPrefix}_Item-empty" style="grid-column:1/-1;text-align:center;padding:2.5rem 1rem;">
      <p class="prd-List_Empty fz-14_150">${msg}</p>
    </li>`
  }

  renderError (action) {
    this.setLoading(false)
    if (action === 'offset') {
      this.isLoadingPagination = false
      return // keep existing results; a failed "load more" shouldn't blank the grid
    }
    const msg = window.theme.strings.searchError || 'Something went wrong.'
    const retry = window.theme.strings.searchRetry || 'Try again'
    this.el('product-grid').innerHTML =
      `<li class="${this.classPrefix}_Item ${this.classPrefix}_Item-empty" style="grid-column:1/-1;text-align:center;padding:2.5rem 1rem;">
      <p class="prd-List_Empty fz-14_150">${escapeHtml(msg)} <a href="#" data-gpt-retry class="drw-Search_Link">${escapeHtml(retry)}</a></p>
    </li>`
    // Don't leave a previous successful search's filters/count showing over the error.
    if (this.el('filters')) this.el('filters').innerHTML = ''
    if (this.el('filters-close')) this.el('filters-close').textContent = ''
    if (this.el('results-count')) this.el('results-count').textContent = ''
    if (this.searchFormat === 'drawer') {
      this.classList.toggle('drw-Search-showResults', true)
      this.el('default-panel').setAttribute('aria-hidden', true)
      this.el('products-panel').setAttribute('aria-hidden', false)
    }
  }

  setLoading (isLoading) {
    this.classList.toggle('depict-search-loading', isLoading)
  }

  /** Build drawer-shaped filter groups from the current result set. */
  buildFilters () {
    return this.facetFields
      .map((field) => {
        const facet = FACET_BY_FIELD[field]
        if (!facet) return null

        const counts = new Map()
        for (const product of this.searchProducts) {
          for (const value of facet.extract(product)) {
            if (!value) continue
            counts.set(value, (counts.get(value) ?? 0) + 1)
          }
        }

        const selected = this.selectedFilters.find((f) => f.key === field)
        const options = [...counts.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([value, count]) => ({
            // Escape for the HTML the shared renderer builds. The browser un-escapes
            // data-attributes on read, so the raw value still round-trips to the API;
            // keep the raw `value` for the selected-state comparison below.
            value: escapeHtml(value),
            name: escapeHtml(titleCase(value)),
            count,
            selected: selected ? selected.values.includes(value) : false,
          }))

        if (!options.length) return null
        return {
          id: field,
          key: field,
          name: this.facetLabel(field),
          op: 'in',
          options,
        }
      })
      .filter(Boolean)
  }

  getProductCardHTML (product, index) {
    const formattedProduct = this.formatGptProduct(product)
    return `
      <li class="${this.classPrefix}_Item" data-index="${index}" data-depict-search-el="item" style="--Order_Desktop: ${index + 1}; --Order_Mobile: ${index + 1};">
        ${depictProductCard({ formattedProduct, index, showFlatImages: false })}
      </li>`
  }

  getSuggestionsHTML (suggestions) {
    return suggestions
      .map((suggestion) => {
        // autocomplete marks the COMPLETED portion with **markdown**. Per the
        // BAO design, the typed portion renders bold and the completion stays
        // regular — so bold everything outside the ** markers. The clean text
        // (markers stripped) still backs the click-through attribute.
        const raw = String(suggestion)
        const clean = raw.replace(/\*\*/g, '')
        const safe = escapeHtml(clean)
        const highlighted = raw
          .split(/\*\*(.*?)\*\*/g)
          .map((segment, i) =>
            i % 2 === 1
              ? escapeHtml(segment)
              : `<strong>${escapeHtml(segment)}</strong>`,
          )
          .join('')
        return `<div class="drw-Search_LinkItem">
          <a href="#" class="drw-Search_Link fz-12_160" data-gpt-suggestion="${safe}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" width="10px" height="10px">
  <path d="M7.5 1.5C4.18629 1.5 1.5 4.18629 1.5 7.5C1.5 10.8137 4.18629 13.5 7.5 13.5C10.8137 13.5 13.5 10.8137 13.5 7.5C13.5 4.18629 10.8137 1.5 7.5 1.5Z" stroke="currentColor"/>
  <rect x="11" y="12.1313" width="1.6" height="4" transform="rotate(-45 11 12.1313)" fill="currentColor"/>
</svg>

            ${highlighted}
          </a>
        </div>`
      })
      .join('')
  }

  onFilterChange (event) {
    const checkbox = event.target.closest('[data-depict-search-el="checkbox"]')
    if (!checkbox) return

    const key = checkbox.dataset.filterKey
    const value = checkbox.dataset.filterOptionValue
    const op = checkbox.dataset.filterOp

    const current = this.selectedFilters.find((f) => f.key === key)
    if (current) {
      if (checkbox.checked && !current.values.includes(value)) {
        current.values.push(value)
      } else if (!checkbox.checked) {
        current.values = current.values.filter((v) => v !== value)
        if (!current.values.length) {
          this.selectedFilters = this.selectedFilters.filter(
            (f) => f !== current,
          )
        }
      }
    } else if (checkbox.checked) {
      this.selectedFilters.push({ key, values: [value], op })
    }

    this.page = 1
    this.getSearchResults('default')
  }

  handleMoreLessToggle (toggleEl) {
    const group = this.querySelector(
      `[data-depict-search-el="group"][data-key="${toggleEl.dataset.key}"]`,
    )
    const isExpanded = group.classList.toggle('drw-Filters_Group-expanded')
    if (isExpanded) {
      this.expandedFilters.push({ key: toggleEl.dataset.key })
    } else {
      this.expandedFilters = this.expandedFilters.filter(
        (f) => f.key !== toggleEl.dataset.key,
      )
    }
  }

  resetAllFilters () {
    this.selectedFilters = []
    this.page = 1
    this.getSearchResults('default')
  }

  resetSearch () {
    // Supersede any in-flight request so a late response can't re-open a drawer
    // the shopper just cleared (bump the seq guard AND abort the fetch).
    this.requestSeq++
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.setLoading(false)
    this.isLoadingPagination = false
    this.searchProducts = []
    this.selectedFilters = []
    if (this.searchFormat !== 'drawer') return
    this.query = ''
    this.classList.toggle('drw-Search-showResults', false)
    this.el('default-panel').setAttribute('aria-hidden', false)
    this.el('products-panel').setAttribute('aria-hidden', true)
    this.el('suggestions-panel').setAttribute('aria-hidden', true)
  }

  setupPaginationObserver () {
    const grid = this.el('product-grid')
    if (!grid) return

    this.paginationSentinel = document.createElement('div')
    this.paginationSentinel.setAttribute('aria-hidden', 'true')
    this.paginationSentinel.style.cssText = 'width:100%;height:1px;'
    grid.insertAdjacentElement('afterend', this.paginationSentinel)

    const root =
      this.searchFormat === 'drawer' ? this.el('products-panel') : null

    this.paginationObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        // Debounce bursts of intersection callbacks to one loadNextPage per frame.
        if (this.isTicking) return
        this.isTicking = true
        requestAnimationFrame(() => {
          this.isTicking = false
          this.loadNextPage()
        })
      },
      { root, rootMargin: `${PAGINATION_THRESHOLD_PX}px 0px` },
    )
    this.paginationObserver.observe(this.paginationSentinel)
  }

  loadNextPage () {
    if (!this.hasMore || this.allProductsLoaded || this.isLoadingPagination) {
      return
    }
    if (!this.query || this.query.length < MIN_QUERY_LENGTH) return

    this.isLoadingPagination = true
    this.page += 1
    this.getSearchResults('offset')
  }
}

if (!customElements.get('depict-search')) {
  customElements.define('depict-search', DepictGptSearch)
}
